import { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import Button from "core/buttons/button/Button";
import Icon from "core/icon/Icon";
import InputDate from "core/inputs/input-date/InputDate";
import InputText from "core/inputs/input-text/InputText";
import { Typography } from "core/typography/Typography";
import {
  DELIVERY_NOTE_FILTERS_ENABLED,
  POPUP_STATUS,
  QUERY_STATUS,
} from "constants/constants";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_GENERATE_ACTIONS,
  DELIVERY_NOTE_GENERATE_LABELS,
  DELIVERY_NOTE_FILTERS_DEFAULTS,
} from "constants/forms";
import {
  DELIVERY_NOTE_LINE_REQUIRED,
  DELIVERY_NOTE_NUMBER_ERROR,
} from "constants/popup-codes";
import InfoContext from "pages/InfoContext";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useUserContext } from "api/user/UserProvider";
import {
  buildDeliveryNoteContentPayload,
  buildDeliveryNoteCopyPayload,
  buildDeliveryNoteDocumentData,
  buildDeliveryNotePayload,
  buildDeliveryNoteRenderNote,
  buildDraftFromNote,
  filterTemplates,
  getCurrencySymbol,
  hasActiveDeliveryNoteFilters,
} from "utils/deliveryNoteHelper";
import DeliveryNoteFilters from "./DeliveryNoteFilters";
import DeliveryNoteTemplatePicker from "./DeliveryNoteTemplatePicker";
import DeliveryNoteLinesBlock from "./DeliveryNoteLinesBlock";
import DeliveryNoteDocumentPreview from "./DeliveryNoteDocumentPreview";
import useDeliveryNoteDocumentImages from "./useDeliveryNoteDocumentImages";
import useDeliveryNotePdfExport from "./useDeliveryNotePdfExport";

const EMPTY_DRAFT = {
  template: null,
  number: "",
  orderNumber: "",
  deliveryDate: "",
  lines: [],
};

const DeliveryNoteGenerator = ({
  templates,
  loadTemplatesStatus,
  isSaveNotePending,
  mintDeliveryNoteNumber,
  createDeliveryNote,
  note,
  updateDeliveryNote,
  onEditionDone,
}) => {
  const { setCode, setStatus, confirmAction } = useContext(InfoContext);
  const { lang } = useUserContext();
  const { currencyListGetter, currencyList } = useCurrencyContext();
  const [draft, setDraft] = useState(() =>
    note ? buildDraftFromNote(note) : EMPTY_DRAFT
  );
  const [isEditing, setIsEditing] = useState(!!note?.id);
  const [templateFilters, setTemplateFilters] = useState(
    DELIVERY_NOTE_FILTERS_DEFAULTS
  );
  const lineKeyRef = useRef(0);
  const numberRequestRef = useRef(0);

  useEffect(() => {
    currencyListGetter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { template } = draft;

  const currencySymbol = getCurrencySymbol({
    currencyList,
    currencyId: template?.currency_id,
  });

  const documentData = useMemo(
    () => buildDeliveryNoteDocumentData({ draft, lang }),
    [draft, lang]
  );

  const { snapshot, resolveImage } = useDeliveryNoteDocumentImages(template);

  const hasActiveFilters = hasActiveDeliveryNoteFilters(templateFilters);
  const filteredTemplates = useMemo(
    () => filterTemplates(templates, templateFilters),
    [templates, templateFilters]
  );

  const documentNote = useMemo(
    () =>
      buildDeliveryNoteRenderNote({
        note: documentData,
        snapshot,
        currencySymbol,
      }),
    [documentData, snapshot, currencySymbol]
  );

  const raise = (code) => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(code);
  };

  const { isExporting, exportRef, exportPdf } = useDeliveryNotePdfExport();

  const editDraft = (patch) =>
    setDraft((current) => ({ ...current, ...patch }));

  const buildBlankLine = () => ({
    key: `line-${++lineKeyRef.current}`,
    cells: {},
  });

  const selectTemplate = async (nextTemplate) => {
    const isSameTemplate = nextTemplate.id === template?.id;
    if (isSameTemplate && draft.number) return;
    if (!isSameTemplate) {
      setDraft({
        ...EMPTY_DRAFT,
        template: nextTemplate,
        lines: [buildBlankLine()],
      });
    }

    const requestId = ++numberRequestRef.current;
    const number = await mintDeliveryNoteNumber({
      templateId: nextTemplate.id,
    });
    if (numberRequestRef.current !== requestId) return;
    if (!number) raise(DELIVERY_NOTE_NUMBER_ERROR);
    setDraft((current) => ({ ...current, number: number || "" }));
  };

  const addLine = () => {
    const line = buildBlankLine();
    setDraft((current) => ({
      ...current,
      lines: [...current.lines, line],
    }));
  };

  const removeLine = (lineIndex) =>
    setDraft((current) => ({
      ...current,
      lines: current.lines.filter((_, index) => index !== lineIndex),
    }));

  const changeCell = ({ lineIndex, position, field, value }) =>
    setDraft((current) => ({
      ...current,
      lines: current.lines.map((line, index) =>
        index === lineIndex
          ? {
              ...line,
              cells: {
                ...line.cells,
                [position]: { ...line.cells[position], [field]: value },
              },
            }
          : line
      ),
    }));

  const runSubmit = async ({ buildPayload, submit, messages }) => {
    const data = buildPayload();
    if (!data.lines.length) {
      raise(DELIVERY_NOTE_LINE_REQUIRED);
      return;
    }
    const result = await submit(data);
    if (!result?.note) {
      confirmAction({ status: QUERY_STATUS.ERROR, messages });
      return;
    }
    confirmAction({
      status: QUERY_STATUS.SUCCESS,
      messages,
      onClose: onEditionDone,
    });
  };

  const saveNote = () =>
    runSubmit({
      buildPayload: () => buildDeliveryNotePayload({ template, draft }),
      submit: (data) => createDeliveryNote({ data }),
      messages: DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.noteCreate,
    });

  const updateNote = () =>
    runSubmit({
      buildPayload: () => buildDeliveryNoteContentPayload({ draft }),
      submit: (data) => updateDeliveryNote({ id: note.id, data }),
      messages: DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.noteUpdate,
    });

  const duplicateNote = () =>
    runSubmit({
      buildPayload: () => buildDeliveryNoteCopyPayload({ note, draft }),
      submit: (data) => createDeliveryNote({ data }),
      messages: DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.noteDuplicate,
    });

  const resetAll = () => {
    numberRequestRef.current += 1;
    setDraft(EMPTY_DRAFT);
    setIsEditing(false);
  };

  return (
    <div className="delivery-note-generate">
      <Typography
        className="title-3 violet text-uppercase"
        type="h1"
        label={<FormattedMessage {...DELIVERY_NOTE_GENERATE_LABELS.title} />}
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={<FormattedMessage {...DELIVERY_NOTE_GENERATE_LABELS.subtitle} />}
      />

      {DELIVERY_NOTE_FILTERS_ENABLED && !isEditing && (
        <DeliveryNoteFilters
          filters={templateFilters}
          setFilters={setTemplateFilters}
          hasActiveFilters={hasActiveFilters}
          idPrefix="delivery-note-generate-template"
        />
      )}

      <div className="delivery-note-creation">
        <div className="delivery-note-form">
          {isEditing ? (
            <div className="delivery-note-generate__edit-header">
              <div
                className="delivery-note-generate__edit-notice"
                id="delivery-note-generate-edit-notice"
              >
                <Icon icon="warning" color="gamboge" width={16} height={16} />
                <FormattedMessage
                  {...DELIVERY_NOTE_GENERATE_LABELS.editNotice}
                  values={{ number: draft.number }}
                />
              </div>
              <Typography
                className="text-3 violet"
                type="p"
                label={
                  <FormattedMessage
                    {...DELIVERY_NOTE_GENERATE_LABELS.editedTemplate}
                    values={{ name: template?.name }}
                  />
                }
              />
            </div>
          ) : (
            <DeliveryNoteTemplatePicker
              templates={filteredTemplates}
              hasActiveFilters={hasActiveFilters}
              loadStatus={loadTemplatesStatus}
              selectedTemplateId={template?.id}
              onSelect={selectTemplate}
            />
          )}

          {template && (
            <>
              <section
                className="delivery-note-form__section"
                id="delivery-note-info-block"
              >
                <Typography
                  className="title-3 violet text-uppercase"
                  type="h2"
                  label={
                    <FormattedMessage
                      {...DELIVERY_NOTE_GENERATE_LABELS.infoBlock}
                    />
                  }
                />
                <div className="delivery-note-grid">
                  <div className="line-1">
                    <div className="cell-padding">
                      <InputText
                        id="delivery-note-order-number"
                        dataTestId="delivery-note-order-number"
                        label={DELIVERY_NOTE_GENERATE_LABELS.orderNumber}
                        value={draft.orderNumber}
                        onChange={(e) =>
                          editDraft({ orderNumber: e.target.value })
                        }
                      />
                    </div>
                    <div className="cell-padding">
                      <InputText
                        id="delivery-note-number"
                        dataTestId="delivery-note-number"
                        label={DELIVERY_NOTE_GENERATE_LABELS.number}
                        value={draft.number}
                        readOnly
                      />
                    </div>
                    <div className="cell-padding">
                      <InputDate
                        id="delivery-note-delivery-date"
                        label={DELIVERY_NOTE_GENERATE_LABELS.deliveryDate}
                        lang={lang}
                        value={draft.deliveryDate}
                        onChange={(e) =>
                          editDraft({ deliveryDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </section>

              <DeliveryNoteLinesBlock
                columns={template.columns}
                lines={draft.lines}
                currencySymbol={currencySymbol}
                onAddLine={addLine}
                onRemoveLine={removeLine}
                onCellChange={changeCell}
              />

              <div className="delivery-note-generate__buttons">
                <Button
                  type="button"
                  className="btn-small btn-outline"
                  onClick={resetAll}
                  id="delivery-note-generate-cancel"
                >
                  <FormattedMessage
                    {...DELIVERY_NOTE_GENERATE_ACTIONS.cancel}
                  />
                </Button>
                {isEditing && (
                  <Button
                    type="button"
                    color="btn-small delivery-note-generate__update"
                    onClick={updateNote}
                    disabled={isSaveNotePending}
                    id="delivery-note-generate-update"
                  >
                    <FormattedMessage
                      {...DELIVERY_NOTE_GENERATE_ACTIONS.update}
                    />
                  </Button>
                )}
                <Button
                  type="button"
                  color="btn-small gray-700"
                  onClick={isEditing ? duplicateNote : saveNote}
                  disabled={isSaveNotePending}
                  id="delivery-note-generate-save"
                >
                  <FormattedMessage
                    {...(isEditing
                      ? DELIVERY_NOTE_GENERATE_ACTIONS.saveAsNew
                      : DELIVERY_NOTE_GENERATE_ACTIONS.save)}
                  />
                </Button>
                <Button
                  type="button"
                  className="btn-small btn-outline"
                  onClick={() => exportPdf({ template, number: draft.number })}
                  disabled={isExporting || !draft.number}
                  id="delivery-note-generate-pdf"
                >
                  <FormattedMessage {...DELIVERY_NOTE_GENERATE_ACTIONS.pdf} />
                </Button>
              </div>
            </>
          )}
        </div>

        <DeliveryNoteDocumentPreview
          snapshot={snapshot}
          resolveImage={resolveImage}
          note={documentNote}
          isExporting={isExporting}
          exportRef={exportRef}
        />
      </div>
    </div>
  );
};

DeliveryNoteGenerator.propTypes = {
  templates: PropTypes.array,
  loadTemplatesStatus: PropTypes.string,
  isSaveNotePending: PropTypes.bool,
  mintDeliveryNoteNumber: PropTypes.func.isRequired,
  createDeliveryNote: PropTypes.func.isRequired,
  note: PropTypes.object,
  updateDeliveryNote: PropTypes.func.isRequired,
  onEditionDone: PropTypes.func.isRequired,
};

export default DeliveryNoteGenerator;
